import React, { useState, useEffect } from 'react';
import { CreditCard, CheckCircle, Clock, AlertCircle, Download, Eye, Filter, Mail } from 'lucide-react';
import clientPaymentService from '../services/clientPaymentService';
import ClientPaymentModal from './ClientPaymentModal';
import { telechargerFacturePDF } from '../services/invoicePdfService';

/**
 * Composant d'onglet facturation pour les clients
 * Affiche les factures et permet le paiement
 */
const ClientBillingTab = ({ db, client, currentUser }) => {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'paid', 'pending', 'overdue'
  const [selectedInvoiceForDetails, setSelectedInvoiceForDetails] = useState(null);

  useEffect(() => {
    loadData();
  }, [client, refreshKey]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Charger les données en parallèle
      const [invoicesData, statsData] = await Promise.all([
        clientPaymentService.getClientInvoices(client.id),
        clientPaymentService.getClientPaymentStats(client.id)
      ]);

      setInvoices(invoicesData);
      setStats(statsData);

    } catch (error) {
      console.error('Erreur lors du chargement des données de facturation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setSelectedInvoice(null);
    // Recharger les données pour mettre à jour l'interface
    setRefreshKey(prev => prev + 1);
  };

  const handlePaymentError = (error) => {
    console.error('Erreur de paiement:', error);
    // L'erreur est gérée dans le modal
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoiceForDetails(invoice);
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      // Adapter les données de clientInvoices au format attendu par le service PDF
      const invoiceForPDF = {
        id: invoice.invoiceNumber || invoice.id, // Utiliser le numéro de facture (FAC-000001) plutôt que l'ID du document
        dateGeneration: invoice.createdAt,
        statut: invoice.status === 'paid' ? 'payée' :
                invoice.status === 'sent' ? 'envoyée' :
                invoice.status === 'overdue' ? 'en_attente' : 'generée',
        client: {
          nom: client.firstName && client.lastName ? `${client.firstName} ${client.lastName}` : client.email,
          email: client.email,
          telephone: client.phoneNumber || ''
        },
        clientId: client.id,
        periode: {
          debut: invoice.periodStart || invoice.createdAt,
          fin: invoice.periodEnd || invoice.createdAt,
          libelle: invoice.productDescription || invoice.description || 'Facturation'
        },
        calcul: {
          nombreRendezVous: 1,
          totalHeures: 1,
          fraisMandatUnitaire: invoice.amount / 100,
          montantTotal: invoice.amount / 100,
          rendezVousDetails: []
        },
        paidAt: invoice.paidAt
      };

      await telechargerFacturePDF(invoiceForPDF);
    } catch (error) {
      console.error('Erreur lors du téléchargement du PDF:', error);
      alert('Erreur lors du téléchargement du PDF');
    }
  };

  const formatPrice = (price) => {
    return (price / 100).toFixed(2) + ' €';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp.toDate()).toLocaleDateString('fr-FR');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return {
          label: 'Payée',
          color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
          icon: CheckCircle
        };
      case 'pending':
        return {
          label: 'En attente',
          color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200',
          icon: Clock
        };
      case 'sent':
        return {
          label: 'Envoyée',
          color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
          icon: Mail
        };
      case 'overdue':
        return {
          label: 'En retard',
          color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
          icon: AlertCircle
        };
      default:
        return {
          label: status,
          color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
          icon: Clock
        };
    }
  };

  // Filtrer les factures selon le statut sélectionné
  const filteredInvoices = invoices.filter(invoice => {
    if (statusFilter === 'all') return true;

    // Le filtre "pending" inclut aussi les factures "sent" et "overdue"
    if (statusFilter === 'pending') {
      return invoice.status === 'pending' || invoice.status === 'sent' || invoice.status === 'overdue';
    }

    return invoice.status === statusFilter;
  });

  // Calculer les compteurs pour chaque statut
  const statusCounts = {
    all: invoices.length,
    paid: invoices.filter(inv => inv.status === 'paid').length,
    pending: invoices.filter(inv =>
      inv.status === 'pending' || inv.status === 'sent' || inv.status === 'overdue'
    ).length,
    overdue: invoices.filter(inv => inv.status === 'overdue').length
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement des factures...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec statistiques */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="flex items-center mb-6">
          <CreditCard className="w-8 h-8 text-blue-500 mr-3" />
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Mes factures
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Consultez et payez vos factures en toute simplicité
            </p>
          </div>
        </div>

        {/* Statistiques */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 dark:text-blue-400">Total payé</p>
                  <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                    {stats.totalPaid.toFixed(2)} €
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-blue-500" />
              </div>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600 dark:text-yellow-400">En attente</p>
                  <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
                    {stats.totalPending.toFixed(2)} €
                  </p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 dark:text-green-400">Factures payées</p>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                    {stats.paidInvoices}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Liste des factures */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Mes factures ({filteredInvoices.length})
              </h3>
              {invoices.filter(inv => inv.status === 'pending').length > 0 && (
                <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 rounded-full text-sm font-medium">
                  {invoices.filter(inv => inv.status === 'pending').length} en attente
                </span>
              )}
            </div>

            {/* Filtres par statut */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  statusFilter === 'all'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <Filter className="w-4 h-4" />
                <span>Toutes</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  statusFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}>
                  {statusCounts.all}
                </span>
              </button>

              <button
                onClick={() => setStatusFilter('paid')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  statusFilter === 'paid'
                    ? 'bg-green-500 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <CheckCircle className="w-4 h-4" />
                <span>Payées</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  statusFilter === 'paid'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}>
                  {statusCounts.paid}
                </span>
              </button>

              <button
                onClick={() => setStatusFilter('pending')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  statusFilter === 'pending'
                    ? 'bg-yellow-500 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <Clock className="w-4 h-4" />
                <span>En attente</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  statusFilter === 'pending'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}>
                  {statusCounts.pending}
                </span>
              </button>

              <button
                onClick={() => setStatusFilter('overdue')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  statusFilter === 'overdue'
                    ? 'bg-red-500 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <AlertCircle className="w-4 h-4" />
                <span>En retard</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  statusFilter === 'overdue'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}>
                  {statusCounts.overdue}
                </span>
              </button>
            </div>
          </div>
        </div>

        {filteredInvoices.length === 0 ? (
          <div className="p-12 text-center">
            <CreditCard className="w-20 h-20 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h4 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
              {invoices.length === 0 ? 'Aucune facture' : 'Aucune facture pour ce filtre'}
            </h4>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
              {invoices.length === 0
                ? 'Vous n\'avez aucune facture pour le moment. Les factures générées par votre gestionnaire apparaîtront ici.'
                : 'Aucune facture ne correspond au filtre sélectionné. Essayez un autre filtre.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredInvoices.map((invoice) => {
              const statusBadge = getStatusBadge(invoice.status);
              const StatusIcon = statusBadge.icon;

              return (
                <div key={invoice.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    {/* Informations de la facture */}
                    <div className="flex-1">
                      <div className="flex items-start space-x-4 mb-3">
                        <div className={`p-3 rounded-lg ${
                          invoice.status === 'paid'
                            ? 'bg-green-100 dark:bg-green-900/30'
                            : invoice.status === 'pending'
                            ? 'bg-orange-100 dark:bg-orange-900/30'
                            : invoice.status === 'sent'
                            ? 'bg-blue-100 dark:bg-blue-900/30'
                            : 'bg-red-100 dark:bg-red-900/30'
                        }`}>
                          <CreditCard className={`w-6 h-6 ${
                            invoice.status === 'paid'
                              ? 'text-green-600 dark:text-green-400'
                              : invoice.status === 'pending'
                              ? 'text-orange-600 dark:text-orange-400'
                              : invoice.status === 'sent'
                              ? 'text-blue-600 dark:text-blue-400'
                              : 'text-red-600 dark:text-red-400'
                          }`} />
                        </div>

                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                              {invoice.productName}
                            </h4>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusBadge.label}
                            </span>
                          </div>

                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                            {invoice.productDescription}
                          </p>

                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                            <div className="flex items-center text-gray-600 dark:text-gray-400">
                              <span className="font-medium text-gray-900 dark:text-white mr-2">N°:</span>
                              <span className="font-mono">{invoice.invoiceNumber}</span>
                            </div>
                            <div className="flex items-center text-gray-600 dark:text-gray-400">
                              <span className="font-medium text-gray-900 dark:text-white mr-2">Émise le:</span>
                              {formatDate(invoice.createdAt)}
                            </div>
                            <div className="flex items-center text-gray-600 dark:text-gray-400">
                              <span className="font-medium text-gray-900 dark:text-white mr-2">Échéance:</span>
                              {formatDate(invoice.dueDate)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Montant et actions */}
                    <div className="flex flex-col items-end gap-3 lg:ml-6">
                      <div className="text-right">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Montant</p>
                        <p className="text-3xl font-bold text-gray-900 dark:text-white">
                          {formatPrice(invoice.amount)}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2 justify-end">
                        {(invoice.status === 'pending' || invoice.status === 'sent' || invoice.status === 'overdue') && (
                          <button
                            onClick={() => handlePayInvoice(invoice)}
                            className="flex items-center px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors font-medium shadow-sm hover:shadow-md"
                          >
                            <CreditCard className="w-4 h-4 mr-2" />
                            Payer maintenant
                          </button>
                        )}

                        {invoice.status === 'paid' && invoice.paidAt && (
                          <div className="text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-lg">
                            Payée le {formatDate(invoice.paidAt)}
                          </div>
                        )}

                        <button
                          onClick={() => handleViewInvoice(invoice)}
                          className="flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="w-4 h-4 mr-1.5" />
                          <span className="hidden sm:inline">Détails</span>
                        </button>

                        <button
                          onClick={() => handleDownloadPDF(invoice)}
                          className="flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          title="Télécharger"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de paiement */}
      {showPaymentModal && selectedInvoice && (
        <ClientPaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          invoice={selectedInvoice}
          clientEmail={client.email}
          clientName={`${client.firstName} ${client.lastName}`}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
        />
      )}

      {/* Modal détails facture */}
      {selectedInvoiceForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Détails de la facture
                </h3>
                <button
                  onClick={() => setSelectedInvoiceForDetails(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            <div className="p-4 md:p-6 space-y-4 md:space-y-6">
              {/* Informations générales */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm md:text-base">Informations générales</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Numéro de facture:</span>
                    <div className="font-mono text-gray-900 dark:text-white font-semibold">
                      {selectedInvoiceForDetails.invoiceNumber || selectedInvoiceForDetails.id}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Statut:</span>
                    <div className="mt-1">
                      {(() => {
                        const badge = getStatusBadge(selectedInvoiceForDetails.status);
                        const Icon = badge.icon;
                        return (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                            <Icon className="w-3 h-3 mr-1" />
                            {badge.label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Date de création:</span>
                    <div className="text-gray-900 dark:text-white">{formatDate(selectedInvoiceForDetails.createdAt)}</div>
                  </div>
                  {selectedInvoiceForDetails.dueDate && selectedInvoiceForDetails.status !== 'paid' && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Date d'échéance:</span>
                      <div className="text-gray-900 dark:text-white">{formatDate(selectedInvoiceForDetails.dueDate)}</div>
                    </div>
                  )}
                  {selectedInvoiceForDetails.paidAt && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Date de paiement:</span>
                      <div className="text-gray-900 dark:text-white">{formatDate(selectedInvoiceForDetails.paidAt)}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Détails du produit/service */}
              {(selectedInvoiceForDetails.productName || selectedInvoiceForDetails.productDescription) && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm md:text-base">Détails</h4>
                  <div className="bg-gray-50 dark:bg-gray-900 p-3 md:p-4 rounded-lg">
                    {selectedInvoiceForDetails.productName && (
                      <div className="mb-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Service:</span>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {selectedInvoiceForDetails.productName}
                        </p>
                      </div>
                    )}
                    {selectedInvoiceForDetails.productDescription && (
                      <div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Description:</span>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {selectedInvoiceForDetails.productDescription}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Montant */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm md:text-base">Montant</h4>
                <div className="bg-gray-50 dark:bg-gray-900 p-3 md:p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 dark:text-gray-400">Montant total:</span>
                    <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {formatPrice(selectedInvoiceForDetails.amount)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Informations de paiement */}
              {selectedInvoiceForDetails.status === 'paid' && selectedInvoiceForDetails.paidAt && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm md:text-base">Informations de paiement</h4>
                  <div className="bg-green-50 dark:bg-green-900/20 p-3 md:p-4 rounded-lg">
                    <div className="flex items-center text-green-700 dark:text-green-300">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      <span className="font-medium">Facture payée le {formatDate(selectedInvoiceForDetails.paidAt)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 md:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex gap-3">
              <button
                onClick={() => handleDownloadPDF(selectedInvoiceForDetails)}
                className="flex-1 px-4 py-2 md:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm md:text-base flex items-center justify-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger PDF
              </button>
              <button
                onClick={() => setSelectedInvoiceForDetails(null)}
                className="flex-1 px-4 py-2 md:py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm md:text-base"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientBillingTab;
